import "./BrowseSales.scss";

import {
  AppBar,
  Box,
  Grid,
  isWidthUp,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  withWidth,
} from "@material-ui/core";
import NoImageAvailable from "common/assets/images/no-image-available.jpg";
import { File } from "common/backend/generated/file-model";
import { Sale, SaleID } from "common/backend/generated/sale-model";
import { AssetTypes } from "common/backend/types/utils";
import { GetSales } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { GetSaleManageable, SaleManageable } from "common/store/sale/types";
import { StoreState } from "common/store/types";
import { UserManageable } from "common/store/user/types";
import { ScreenWidth } from "common/utils/width";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import { ReisterAdviceBanner } from "pages/idnow-status/IDNowStatus";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";

import { SaleInfo } from "./SaleInfo";

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const indicatorStyle = makeStyles({
  root: {
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
    height: "90px",
  },
  indicator: {
    backgroundColor: "#0059E2",
    height: 4,
  },
});

const tabStyle = makeStyles({
  root: { height: "90px" },
  wrapper: {
    fontSize: "24px",
    fontWeight: 500,
  },
});

const appBarStyle = makeStyles({
  root: { marginBottom: "100px" },
  colorPrimary: {
    backgroundColor: "transparent",
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
  },
});

interface Props {
  sale?: SaleManageable;
  screenWidth: ScreenWidth;
  selected: boolean;
}

const SaleCard = ({
  sale, screenWidth, selected
}: Props) => {
  const featuredImage =
    sale && sale.images && sale.images.length > 0
      ? sale.images.find((image: File) => image.isFeatured)
      : undefined;

  const featuredImageURL = featuredImage ? featuredImage.url : NoImageAvailable;

  return (
    <>
      {sale && (!selected || isWidthUp(
        "md",
        screenWidth
      )) &&
        <Grid
          item
          container
          justify="center"
          className={"sale" + (selected ? "--selected" : "")}
        >
          <Grid
            item
            className={"image-container" + (selected ? "--selected" : "")}
          >
            <img
              className={selected ? "selected" : ""}
              src={featuredImageURL}
              alt=""
            />
          </Grid>

          <Grid
            item
            className={"info-container" + (selected ? "--selected" : "")}
          >
            <SaleInfo sale={sale} selected={selected} />
          </Grid>
        </Grid>
      }
    </>
  );
};

interface BrowseSalesStateItem {
  dataLength: number;
  lastSaleID?: SaleID;
  hasMore: boolean;
}

const getEmptyBrowseSalesStateItem = (): BrowseSalesStateItem => {
  return {
    dataLength: 0,
    lastSaleID: undefined,
    hasMore: true,
  };
};

function BrowseSales({ width }: any) {
  // State
  const [assetType, setAssetType] = React.useState(AssetTypes.LOAN);
  const [shouldFetch, setShouldFetch] = React.useState(true);
  const [state, setState] = React.useState<BrowseSalesStateItem>(getEmptyBrowseSalesStateItem());

  // Hooks
  const { t } = useTranslation();

  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);
  const assetTypeSales = useSelector<StoreState, Array<SaleManageable>>((state: StoreState) => state.sales.get(assetType) || []);

  const { execute: getSales } = useCustomQuery(GetSales({
    onSuccess: (data: Array<Sale>) =>
      setState((state) => ({
        dataLength: state.dataLength + data.length,
        lastSaleID: data.length > 0 ? data[data.length - 1].id : undefined,
        hasMore: data.length > 0,
      })),
  }));

  const appBar = appBarStyle();
  const indicator = indicatorStyle();
  const tab = tabStyle();

  // Helpers
  const fetchData = useCallback(
    () => {
      getSales({
        variables: {
          input: new GetSaleManageable({
            startAt: state.lastSaleID,
            limit: 3,
          }).serialize(),
        },
      });
    },
    [getSales, state.lastSaleID]
  );

  // Actions
  useEffect(
    () => {
      if (shouldFetch) {
        setShouldFetch(false);
        fetchData();
      }
    },
    [shouldFetch, setShouldFetch, fetchData]
  );

  function TabPanel(props: TabPanelProps) {
    const {
      children, index, ...other
    } = props;

    return (
      <div
        role="tabpanel"
        hidden={assetType !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {assetType === index && <div>{children}</div>}
      </div>
    );
  }

  function a11yProps(index: any) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  return (
    <Box>
      <Navbar />

      <ContentWrapper paddingTop="0px">
        <InfiniteScroll
          className="browse-sales"
          dataLength={state.dataLength}
          next={fetchData}
          hasMore={state.hasMore}
          loader={<h4>Loading...</h4>}
        >
          <Box className="browse-sales">
            <Box
              bgcolor="transparent"
              marginX="26px"
              marginY="100px"
              display="flex"
              height="85px"
              justifyContent="center"
              alignItems="center"
            >
              <Typography color="textSecondary" variant="h1" className="title">
                {t("Browse Offerings")}
              </Typography>
            </Box>

            <ReisterAdviceBanner
              status={storageUserDetails.idNowStatus}
            />

            <Grid
              item
              container
              direction="row"
              justify="center"
              alignItems="center"
              className={"content"}
            >
              <AppBar position="static" classes={appBar}>
                <Tabs
                  centered
                  variant="fullWidth"
                  classes={indicator}
                  value={assetType}
                  onChange={(
                    _: any, value: number
                  ) =>
                    setAssetType(Object.values(AssetTypes)[value])
                  }
                  aria-label="simple tabs example"
                >
                  <Tab
                    value={AssetTypes.LOAN}
                    label={t("Loans")}
                    classes={tab}
                    {...a11yProps(0)}
                    style={
                      assetType === AssetTypes.LOAN
                        ? { color: "#0059E2" }
                        : { color: "#696F74" }
                    }
                  />
                </Tabs>
              </AppBar>

              <TabPanel value={assetType} index={AssetTypes.LOAN}>
                <SaleCard
                  selected={true}
                  sale={assetTypeSales[0]}
                  screenWidth={width}
                />

                <Grid container spacing={6}>
                  {assetTypeSales.map((
                    sale: SaleManageable, index: number
                  ) =>
                    <Grid item key={index} lg={4} md={6} sm={12} xs={12}>
                      <SaleCard
                        screenWidth={width}
                        selected={false}
                        sale={sale}
                      />
                    </Grid>)}
                </Grid>
              </TabPanel>
            </Grid>
          </Box>
        </InfiniteScroll>
      </ContentWrapper>
    </Box>
  );
}

export default withWidth()(BrowseSales);
